/**
 * De kop van het midden staat stil: kanaal, onderwerp, pilletje, bron en
 * wachttijd; rechts Sluiten, Spam en Verwijderen. Op de iPhone de
 * terugpijl en in plaats van de drie knoppen Klant, Copiloot en Meer; Meer
 * opent een menu met Sluiten, Spam en Verwijderen (sessie 75, blok 4).
 */
import { useEffect, useRef, useState, type KeyboardEvent as ToetsEvent } from "react";
import { Link } from "react-router-dom";

import { v6Api, type AfrondUitkomst, type V6Navraag, type V6Zaak } from "../api";
import { useV6 } from "../context";
import { Icoon } from "../Icoon";
import { useLive } from "../live";
import { veiligeLink } from "../Rechts/opmaak";
import { useMidden } from "./middenQuery";
import { dagKlok } from "./tijd";
import { foutTekst } from "./vak";
import "@/styles/statusbalk.css";

type Afronding = "sluiten" | "spam" | "verwijderen";

const AFROND_WOORD: Record<Afronding, string> = {
  sluiten: "Afgerond.",
  spam: "Naar spam.",
  verwijderen: "Weggegooid.",
};

const MISLUKT: Record<Afronding, string> = {
  sluiten: "Sluiten mislukt.",
  spam: "Naar spam mislukt.",
  verwijderen: "Weggooien mislukt.",
};

const AFRONDINGEN: { soort: Afronding; woord: string; icoon: "sluit" | "spam" | "prul" }[] = [
  { soort: "sluiten", woord: "Sluiten", icoon: "sluit" },
  { soort: "verwijderen", woord: "Weggooien", icoon: "prul" },
  { soort: "spam", woord: "Spam", icoon: "spam" },
];

/**
 * Sessie 75, blok 4: op de iPhone zijn de bureauknoppen verborgen. Meer
 * opent een klein menu met dezelfde drie afrondingen. Echte knoppen, sluit
 * bij een tik erbuiten, na een keuze en met Escape.
 */
function MeerMenu({
  bezig,
  onKies,
}: {
  bezig: boolean;
  onKies: (soort: Afronding) => void;
}) {
  const [open, setOpen] = useState(false);
  const houder = useRef<HTMLDivElement>(null);
  const knop = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    const buiten = (e: Event) => {
      if (houder.current && !houder.current.contains(e.target as Node)) setOpen(false);
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        knop.current?.focus();
      }
    };
    document.addEventListener("pointerdown", buiten);
    document.addEventListener("keydown", toets);
    return () => {
      document.removeEventListener("pointerdown", buiten);
      document.removeEventListener("keydown", toets);
    };
  }, [open]);

  const pijltjes = (e: ToetsEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
    const nu = items.indexOf(document.activeElement as HTMLButtonElement);
    const stap = e.key === "ArrowDown" ? 1 : -1;
    items[(nu + stap + items.length) % items.length]?.focus();
  };

  return (
    <div className="meerhouder" ref={houder}>
      <button
        ref={knop}
        type="button"
        className="mob meer"
        title="Meer acties"
        aria-label="Meer acties"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Icoon naam="meer" klein />
      </button>
      {open && (
        <div className="meermenu" role="menu" aria-label="Acties" ref={menu} onKeyDown={pijltjes}>
          {AFRONDINGEN.map(({ soort, woord, icoon }) => (
            <button
              key={soort}
              type="button"
              role="menuitem"
              className={soort === "verwijderen" ? "weg" : undefined}
              disabled={bezig}
              onClick={() => {
                setOpen(false);
                onKies(soort);
              }}
            >
              <Icoon naam={icoon} klein />
              {woord}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Keuze 5 van 75: in de kop alleen de live-stand (Nova is nu met de klant bezig). */
export function Statuschip({ zaak }: { zaak: V6Zaak }) {
  const { isLive } = useLive();
  if (zaak.groep === "live" || isLive(zaak.gesprek_ids)) {
    return (
      <span className="v6-chip live">
        <span className="stip" />
        {zaak.groep === "live" ? zaak.status_woord : "Nova antwoordt live"}
      </span>
    );
  }
  return null;
}

/**
 * De naam van de klant: gekoppeld, anders de afzender van de eerste klantmail.
 * Een chat of WhatsApp zonder koppeling heeft geen naam ("Klant" is een
 * plaatshouder); dan staat er alleen het onderwerp, nooit een verzonnen naam.
 */
export function klantNaamVan(zaak: V6Zaak): string | null {
  if (zaak.klant_naam) return zaak.klant_naam;
  const eerste = zaak.berichten.find((b) => b.kant === "klant" && b.soort === "mail" && b.wie.trim());
  return eerste ? eerste.wie : null;
}

/** Binnenkomst: de zaak zelf, anders het eerste klantbericht; anders niets (geen afgeleide waarde). */
export function binnenVan(zaak: V6Zaak, binnen: string | null | undefined): string | null {
  if (binnen) return binnen;
  return zaak.berichten.find((b) => b.kant === "klant")?.moment ?? null;
}

/**
 * TAP-tickets 28-10: per ticket één regel onder de kop. De stand staat er
 * altijd als woord; de link Ticket alleen bij een https-adres.
 */
export function NavraagRegel({ ticket }: { ticket: V6Navraag }) {
  const link = veiligeLink(ticket.url);
  const nummer = ticket.ticket_nummer?.trim().replace(/^#/, "") || null;
  return (
    <div className="m7-navraag" data-testid="zaak-navraag">
      <span className="w">
        Navraag bij vervoerder: {ticket.stand}
        {ticket.dag != null ? <>, dag <span className="num">{ticket.dag}</span></> : null}
        {nummer ? <span className="num"> #{nummer}</span> : null}
      </span>
      {link && (
        <a
          className="m7-ticketknop"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          title={
            nummer
              ? `Ticket #${nummer} openen bij de vervoerder (nieuw tabblad)`
              : "Ticket openen bij de vervoerder (nieuw tabblad)"
          }
        >
          <svg className="m7-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M14 4h6v6" />
            <path d="M20 4 10 14" />
            <path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" />
          </svg>
          Ticket
        </a>
      )}
    </div>
  );
}

export function Kop({
  zaak,
  onTerug,
  onAfgerond,
}: {
  zaak: V6Zaak;
  onTerug: () => void;
  onAfgerond: (tekst: string, uitkomst: AfrondUitkomst) => void;
}) {
  const { openRechts } = useV6();
  const { data: midden } = useMidden(zaak.zaak_id);
  const [bezig, setBezig] = useState<Afronding | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const naam = klantNaamVan(zaak);
  const binnen = binnenVan(zaak, midden?.binnen);
  const belofte = zaak.stand === "afgerond" ? null : midden?.belofte ?? null;

  const rondAf = async (soort: Afronding) => {
    setBezig(soort);
    setFout(null);
    try {
      const uitkomst = await v6Api[soort](zaak.zaak_id);
      onAfgerond(AFROND_WOORD[soort], uitkomst);
    } catch (e) {
      // De reden van de server ("Zaak is al afgerond"), zoals in de lijst.
      setFout(`${MISLUKT[soort]} ${foutTekst(e, "Probeer het opnieuw.")}`);
    } finally {
      setBezig(null);
    }
  };

  return (
    <>
      <header className="kop vo-statusbalkvrij">
        <button type="button" className="terug" title="Terug naar de lijst" onClick={onTerug}>
          <Icoon naam="terug" klein />
        </button>
        <div className="wie">
          <div className="r1">
            <span className="ic">
              <Icoon naam={zaak.kanaal} />
            </span>
            <b>
              {naam && zaak.klant_id ? (
                <Link
                  className="klantnaam"
                  to={`/klanten/${zaak.klant_id}`}
                  title={`Naar de klantkaart van ${naam}`}
                >
                  {naam}
                </Link>
              ) : (
                naam
              )}
              {naam ? ": " : ""}
              {zaak.onderwerp}
            </b>
          </div>
          <div className="sub">
            <Statuschip zaak={zaak} />
            {binnen && (
              <span>
                Binnen <span className="num">{dagKlok(binnen)}</span>
              </span>
            )}
            {belofte && (
              <span className="m7-pil let" data-testid="m7-belofte">
                Belofte: {belofte.wat} vóór {dagKlok(belofte.tot)}
              </span>
            )}
          </div>
        </div>
        <div className="knoppen">
          <button type="button" className="mob" onClick={() => openRechts("klant")}>
            <Icoon naam="klant" klein />
            Klant
          </button>
          <button type="button" className="mob" onClick={() => openRechts("copiloot")}>
            <Icoon naam="copiloot" klein />
            Copiloot
          </button>
          <MeerMenu bezig={bezig !== null} onKies={(soort) => void rondAf(soort)} />
          <button
            type="button"
            className="bureau"
            title="Sluiten"
            disabled={bezig !== null}
            onClick={() => void rondAf("sluiten")}
          >
            <Icoon naam="sluit" klein />
            Sluiten
          </button>
          <button
            type="button"
            className="bureau"
            title="Weggooien"
            disabled={bezig !== null}
            onClick={() => void rondAf("verwijderen")}
          >
            <Icoon naam="prul" klein />
            Weggooien
          </button>
          <button
            type="button"
            className="bureau"
            title="Spam"
            disabled={bezig !== null}
            onClick={() => void rondAf("spam")}
          >
            <Icoon naam="spam" klein />
            Spam
          </button>
        </div>
      </header>
      {/* TAP-tickets: onder de kop over de volle breedte, zodat de knoppen
          niet verschuiven en de regel op de iPhone niet in de naamkolom knelt. */}
      {(zaak.navraag ?? []).length > 0 && (
        <div className="m7-navraagband">
          {(zaak.navraag ?? []).map((t, i) => (
            <NavraagRegel key={`${t.ticket_nummer ?? ""}-${i}`} ticket={t} />
          ))}
        </div>
      )}
      {fout && (
        <div className="v6-melding fout" role="alert">
          <span className="v6-fout">{fout}</span>
        </div>
      )}
    </>
  );
}
